// fakturapp/src/shared/dialogs/ksef-error-details-dialog/ksef-error-details-dialog.component.ts

import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { FakturniaHighlightComponent } from '@fakturnia/components/highlight';

@Component({
  standalone: true,
  imports: [
    CommonModule, 
    MatDialogModule, 
    MatButtonModule, 
    MatIconModule, 
    MatTooltipModule,
    MatTabsModule,
    FakturniaHighlightComponent
  ],
  selector: 'app-ksef-error-details-dialog',
  templateUrl: './ksef-error-details-dialog.component.html',
  styleUrls: ['./ksef-error-details-dialog.component.scss']
})
export class KsefErrorDetailsDialogComponent {
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KsefErrorDetailsDialogData,
    private dialogRef: MatDialogRef<KsefErrorDetailsDialogComponent>
  ) {}
  
  /**
   * Pobierz kod błędu z różnych struktur
   */
  getErrorCode(): string {
    // Brak błędu - stare logi
    if (!this.data.error) return 'N/A';
    
    // Struktura z KSeF batch API (result.status)
    if (this.data.error?.code) return this.data.error.code.toString();
    
    // Alternatywne struktury
    if (this.data.error?.errorCode) return this.data.error.errorCode;
    if (this.data.error?.status?.code) return this.data.error.status.code.toString();
    
    console.warn('Unknown error structure:', this.data.error);
    return 'UNKNOWN';
  }
  
  /**
   * Pobierz komunikat błędu
   */
  getErrorMessage(): string {
    // Brak błędu - stare logi
    if (!this.data.error) {
      return 'Szczegóły błędu niedostępne. Ten log został utworzony przed aktualizacją systemu logowania.';
    }
    
    // Struktura z KSeF batch API (result.status)
    if (this.data.error?.description) return this.data.error.description;
    
    // Alternatywne struktury
    if (this.data.error?.message) return this.data.error.message;
    if (this.data.error?.errorMessage) return this.data.error.errorMessage;
    if (this.data.error?.status?.description) return this.data.error.status.description;
    
    console.warn('Unknown error structure:', this.data.error);
    return 'Nieznany błąd';
  }
  
  /**
   * Sprawdź czy są szczegóły
   */
  hasDetails(): boolean {
    if (!this.data.error) return false;
    
    return !!(
      this.data.error?.details || 
      this.data.error?.errorDetails ||
      this.data.error?.validationErrors
    );
  }
  
  /**
   * Pobierz szczegóły jako string (JSON)
   */
  getDetailsString(): string {
    const details = this.data.error?.details || 
                   this.data.error?.errorDetails || 
                   this.data.error?.validationErrors;
    
    if (!details) return JSON.stringify({ message: 'Brak szczegółów' }, null, 2);
    
    // Zawsze zwracaj jako sformatowany JSON dla syntax highlighting
    if (Array.isArray(details)) {
      return JSON.stringify({ 
        errors: details.map((d, i) => ({
          index: i + 1,
          message: typeof d === 'string' ? d : d
        }))
      }, null, 2);
    }
    
    // Jeśli to string, opakuj w object
    if (typeof details === 'string') {
      return JSON.stringify({ message: details }, null, 2);
    }
    
    // Jeśli to object, sformatuj
    return JSON.stringify(details, null, 2);
  }
  
  /**
   * Pobierz pełny obiekt błędu jako JSON
   */
  getRawErrorString(): string {
    return JSON.stringify(this.data.error, null, 2);
  }
  
  /**
   * Kopiuj błąd do schowka
   */
  copyError() {
    const errorText = JSON.stringify(this.data.error, null, 2);
    navigator.clipboard.writeText(errorText).then(() => {
      console.log('Błąd skopiowany do schowka');
    });
  }

  /**
   * Kopiuj logData do schowka
   */
  copyLogData() {
    const logDataText = JSON.stringify(this.data.logData, null, 2);
    navigator.clipboard.writeText(logDataText).then(() => {
      console.log('Log data skopiowane do schowka');
    });
  }
  
  /**
   * Sprawdź czy XML jest dostępny
   */
  hasXml(): boolean {
    return !!this.data.xml;
  }

  /**
   * Sprawdź czy pełna logData jest dostępna
   */
  hasLogData(): boolean {
    return !!this.data.logData;
  }

  /**
   * Pobierz pełną logData jako JSON string
   */
  getLogDataString(): string {
    if (!this.data.logData) return JSON.stringify({ message: 'Brak danych' }, null, 2);
    return JSON.stringify(this.data.logData, null, 2);
  }
  
  /**
   * Pobierz sformatowany XML
   */
  getFormattedXml(): string {
    if (!this.data.xml) return '';
    
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.data.xml, 'text/xml');
      const parserError = xmlDoc.querySelector('parsererror');
      
      if (parserError) {
        return this.data.xml; // Zwróć raw XML jeśli błąd parsowania
      }
      
      return this.formatXmlNode(xmlDoc, 0);
    } catch (e) {
      console.error('Błąd formatowania XML:', e);
      return this.data.xml;
    }
  }
  
  /**
   * Formatuj node XML rekurencyjnie (8 spacji wcięcia)
   */
  private formatXmlNode(node: Node, level: number): string {
    const indent = '        '; // 8 spacji
    const padding = indent.repeat(level);
    let formatted = '';
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName;
      const attributes = Array.from(element.attributes)
        .map(attr => ` ${attr.name}="${attr.value}"`)
        .join('');
      
      const children = Array.from(element.childNodes);
      const hasOnlyText = children.length === 1 && children[0].nodeType === Node.TEXT_NODE;
      
      if (hasOnlyText) {
        // Inline dla elementów z tylko tekstem
        formatted += `${padding}<${tagName}${attributes}>${children[0].textContent}</${tagName}>\n`;
      } else if (children.length === 0) {
        // Self-closing dla pustych elementów
        formatted += `${padding}<${tagName}${attributes}/>\n`;
      } else {
        // Multi-line dla elementów z dziećmi
        formatted += `${padding}<${tagName}${attributes}>\n`;
        children.forEach(child => {
          formatted += this.formatXmlNode(child, level + 1);
        });
        formatted += `${padding}</${tagName}>\n`;
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        formatted += `${padding}${text}\n`;
      }
    } else if (node.nodeType === Node.DOCUMENT_NODE) {
      // Root document node
      Array.from(node.childNodes).forEach(child => {
        formatted += this.formatXmlNode(child, level);
      });
    }
    
    return formatted;
  }
  
  /**
   * Kopiuj XML do schowka
   */
  copyXml() {
    if (!this.data.xml) return;
    
    navigator.clipboard.writeText(this.data.xml).then(() => {
      console.log('XML skopiowany do schowka');
    });
  }
  
  /**
   * Zamknij dialog
   */
  close() {
    this.dialogRef.close();
  }
  
  /**
   * Pobierz sugestię rozwiązania
   */
  getSuggestion(): string | null {
    const code = this.getErrorCode();
    
    // Brak sugestii dla starych logów
    if (code === 'N/A' || code === 'UNKNOWN') return null;
    
    const suggestions: Record<string, string> = {
      '400': 'Sprawdź poprawność danych faktury. Możliwe błędy w XML lub brakujące wymagane pola.',
      '401': 'Problem z autoryzacją. Sprawdź czy token KSeF jest aktualny.',
      '403': 'Brak uprawnień. Sprawdź czy użytkownik ma prawo do wysyłki faktur.',
      '404': 'Zasób nie znaleziony. Faktura mogła zostać już usunięta z KSeF.',
      '409': 'Konflikt. Faktura o tym numerze mogła już zostać wysłana.',
      '422': 'Błąd walidacji XML. Sprawdź strukturę faktury względem schematu FA(3).',
      '429': 'Przekroczono limit 120 requestów/godzinę. Poczekaj na odnowienie limitu.',
      '450': 'Błąd walidacji schemy XML. Sprawdź strukturę faktury względem schematu FA(3) v1-0E.',
      '500': 'Błąd serwera KSeF. Spróbuj ponownie za chwilę.',
      '503': 'Serwis KSeF niedostępny. Sprawdź status na stronie MF.'
    };
    
    return suggestions[code] || null;
  }
}

export interface KsefErrorDetailsDialogData {
  documentNumber: string;
  documentId: string;
  error: any;
  logData?: any;  // 🔑 Pełna data z logu (do JSON preview)
  xml?: string;  // 🔑 XML faktury
  timestamp?: Date;
  userId?: string;
  userEmail?: string;
}

